import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { IngestPanel } from "./IngestPanel";
import { UserManagement } from "./admin/UserManagement";

interface AdminProps {
  appNameLocked: boolean;
  currentAppName: string;
  homeRefreshSeconds: number;
  homeRefreshLocked: boolean;
  authMode: "oidc" | "local" | "dev";
  currentUserId: number;
}

async function postJson<T>(url: string, body: unknown = {}): Promise<T> {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

interface JobsStats {
  queues: Record<string, { size?: number; error?: string }>;
}

interface UsersResponse {
  users: Array<{
    id: string | number;
    email: string | null;
    display_name: string | null;
    is_admin_cached: boolean;
    last_seen_at: string | null;
    created_at: string;
  }>;
}

export function Admin({
  appNameLocked,
  currentAppName,
  homeRefreshSeconds,
  homeRefreshLocked,
  authMode,
  currentUserId,
}: AdminProps) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [appNameDraft, setAppNameDraft] = useState(currentAppName);
  const [homeRefreshDraft, setHomeRefreshDraft] = useState(String(homeRefreshSeconds));
  const saveAppName = useMutation({
    mutationFn: () => api.setAppName(appNameDraft.trim()),
    onSuccess: () => {
      setMsg(`App name updated to "${appNameDraft.trim()}"`);
      qc.invalidateQueries({ queryKey: ["config"] });
    },
    onError: (err) => setMsg(`App name update failed: ${String(err)}`),
  });
  const saveHomeRefresh = useMutation({
    mutationFn: () => api.setHomeRefreshSeconds(Number(homeRefreshDraft)),
    onSuccess: (data) => {
      setMsg(
        data.value === 0
          ? "All-page auto-shuffle disabled (showing newest first)"
          : `All-page auto-shuffle every ${data.value}s`,
      );
      qc.invalidateQueries({ queryKey: ["config"] });
    },
    onError: (err) => setMsg(`Update failed: ${String(err)}`),
  });
  const jobs = useQuery({
    queryKey: ["admin", "jobs"],
    queryFn: () => getJson<JobsStats>("/api/admin/jobs"),
    refetchInterval: 5000,
  });
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => getJson<UsersResponse>("/api/admin/users"),
  });

  const runOp = (name: string, fn: () => Promise<unknown>) =>
    useMutation({
      mutationFn: fn,
      onSuccess: (data) => {
        setMsg(`${name}: ${JSON.stringify(data)}`);
        qc.invalidateQueries({ queryKey: ["admin"] });
        qc.invalidateQueries({ queryKey: ["media"] });
      },
      onError: (err) => setMsg(`${name} failed: ${String(err)}`),
    });

  const rescan = runOp("Rescan", () => postJson("/api/admin/rescan"));
  const retranscodeAll = runOp("Retranscode all", () => postJson("/api/admin/retranscode", { all: true }));
  const purge = runOp("Purge trash", () => postJson("/api/admin/purge-trash", { olderThanDays: 7 }));
  const redetect = useMutation({
    mutationFn: () => api.redetectAnimated(),
    onSuccess: (r) => {
      window.alert(`Scanned ${r.scanned}, flagged ${r.flagged} animated, ${r.errored} errored.`);
      qc.invalidateQueries({ queryKey: ["admin"] });
      qc.invalidateQueries({ queryKey: ["media"] });
    },
    onError: (err) => setMsg(`Re-detect animation failed: ${String(err)}`),
  });

  return (
    <div className="p-6">
      <h2 className="mb-4 text-2xl font-semibold">Admin</h2>

      <div className="mb-6 rounded border border-zinc-800 bg-zinc-900 p-4">
        <h3 className="mb-2 text-base font-medium">App name</h3>
        <p className="mb-3 text-xs text-zinc-500">
          Shown in the top-left of the header. {appNameLocked ? "Locked by env var APP_NAME — unset it to edit here." : "Stored in the database; takes effect immediately."}
        </p>
        <div className="flex gap-2">
          <input
            value={appNameDraft}
            onChange={(e) => setAppNameDraft(e.target.value)}
            disabled={appNameLocked}
            maxLength={80}
            className="flex-1 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm focus:border-zinc-500 focus:outline-none disabled:opacity-60"
          />
          <button
            onClick={() => saveAppName.mutate()}
            disabled={appNameLocked || saveAppName.isPending || appNameDraft.trim() === "" || appNameDraft.trim() === currentAppName}
            className="rounded bg-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-600 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>

      <div className="mb-6 rounded border border-zinc-800 bg-zinc-900 p-4">
        <h3 className="mb-2 text-base font-medium">All-page auto-shuffle</h3>
        <p className="mb-3 text-xs text-zinc-500">
          Seconds between automatic random shuffles on the All page.{" "}
          <strong>0</strong> disables auto-shuffle (page shows newest-first instead).
          {homeRefreshLocked
            ? " Locked by env var HOME_REFRESH_SECONDS — unset it to edit here."
            : " Stored in the database; takes effect immediately."}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={0}
            max={86400}
            value={homeRefreshDraft}
            onChange={(e) => setHomeRefreshDraft(e.target.value)}
            disabled={homeRefreshLocked}
            className="w-32 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm focus:border-zinc-500 focus:outline-none disabled:opacity-60"
          />
          <span className="text-xs text-zinc-500">
            seconds {homeRefreshDraft && Number(homeRefreshDraft) > 0
              ? `(≈ ${(Number(homeRefreshDraft) / 60).toFixed(1)} min)`
              : ""}
          </span>
          <div className="ml-auto flex gap-1">
            {[300, 600, 1800].map((s) => (
              <button
                key={s}
                onClick={() => setHomeRefreshDraft(String(s))}
                disabled={homeRefreshLocked}
                className="rounded bg-zinc-800 px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-700 disabled:opacity-60"
              >
                {s / 60}m
              </button>
            ))}
            <button
              onClick={() => setHomeRefreshDraft("0")}
              disabled={homeRefreshLocked}
              className="rounded bg-zinc-800 px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-700 disabled:opacity-60"
            >
              off
            </button>
          </div>
          <button
            onClick={() => saveHomeRefresh.mutate()}
            disabled={
              homeRefreshLocked ||
              saveHomeRefresh.isPending ||
              homeRefreshDraft === "" ||
              Number(homeRefreshDraft) === homeRefreshSeconds ||
              !Number.isFinite(Number(homeRefreshDraft))
            }
            className="rounded bg-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-600 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>

      <div className="mb-6">
        <IngestPanel />
      </div>

      <OptimizePanel />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <a
          href="/admin/trash"
          className="rounded border border-zinc-800 bg-zinc-900 p-4 text-left hover:bg-zinc-800"
        >
          <div className="font-medium">🗑 Review trash</div>
          <div className="mt-1 text-xs text-zinc-500">
            Browse soft-deleted media, restore items or hard-delete immediately.
          </div>
        </a>
        <button
          onClick={() => rescan.mutate()}
          disabled={rescan.isPending}
          className="rounded border border-zinc-800 bg-zinc-900 p-4 text-left hover:bg-zinc-800"
        >
          <div className="font-medium">Re-scan library</div>
          <div className="mt-1 text-xs text-zinc-500">Walks /media and ingests anything new.</div>
        </button>
        <button
          onClick={() => {
            if (window.confirm("Re-probe every GIF/WebP file to detect animation? May take a few minutes on a large library.")) {
              redetect.mutate();
            }
          }}
          disabled={redetect.isPending}
          className="rounded border border-zinc-800 bg-zinc-900 p-4 text-left hover:bg-zinc-800"
        >
          <div className="font-medium">Re-detect animation on GIF/WebP</div>
          <div className="mt-1 text-xs text-zinc-500">Re-probes every GIF/WebP and flags animated rows.</div>
        </button>
        <button
          onClick={() => retranscodeAll.mutate()}
          disabled={retranscodeAll.isPending}
          className="rounded border border-zinc-800 bg-zinc-900 p-4 text-left hover:bg-zinc-800"
        >
          <div className="font-medium">Re-transcode all videos</div>
          <div className="mt-1 text-xs text-zinc-500">Drops cache and queues fresh HLS for every video.</div>
        </button>
        <button
          onClick={() => purge.mutate()}
          disabled={purge.isPending}
          className="rounded border border-zinc-800 bg-zinc-900 p-4 text-left hover:bg-zinc-800"
        >
          <div className="font-medium">Purge trash (&gt;7 days)</div>
          <div className="mt-1 text-xs text-zinc-500">Hard-delete soft-deleted media older than 7 days.</div>
        </button>
      </div>

      {msg && (
        <div className="mb-4 rounded border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300">
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded border border-zinc-800 bg-zinc-900 p-4">
          <h3 className="mb-2 text-base font-medium">Queue stats</h3>
          {jobs.isPending ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : jobs.isError ? (
            <div className="text-sm text-red-300">Error: {String(jobs.error)}</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(jobs.data.queues).map(([name, q]) => (
                  <tr key={name} className="border-t border-zinc-800/60">
                    <td className="py-1.5 text-zinc-300">{name}</td>
                    <td className="py-1.5 text-right text-zinc-400">
                      {q.error ?? `${q.size ?? 0} pending`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {authMode !== "local" && (
          <div className="rounded border border-zinc-800 bg-zinc-900 p-4">
            <h3 className="mb-2 text-base font-medium">Users</h3>
            {users.isPending ? (
              <div className="text-sm text-zinc-500">Loading…</div>
            ) : users.isError ? (
              <div className="text-sm text-red-300">Error: {String(users.error)}</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800/60 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <th className="py-1">User</th>
                    <th className="py-1">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.data.users.map((u) => (
                    <tr key={String(u.id)} className="border-t border-zinc-800/60">
                      <td className="py-1.5 text-zinc-300">
                        {u.display_name ?? u.email ?? `user-${u.id}`}
                        {u.is_admin_cached && (
                          <span className="ml-2 rounded bg-amber-700/40 px-1.5 py-0.5 text-[10px] uppercase text-amber-100">
                            admin
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 text-zinc-400">
                        {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {authMode === "oidc" && (
              <div className="mt-2 rounded bg-zinc-800/40 px-3 py-2 text-xs text-zinc-400">
                User accounts are managed by the OIDC provider.
              </div>
            )}
          </div>
        )}
      </div>

      {authMode === "local" && (
        <div className="mt-6">
          <UserManagement currentUserId={currentUserId} />
        </div>
      )}
    </div>
  );
}

function OptimizePanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "optimize"],
    queryFn: () => api.getOptimize(),
    // Poll often enough that the progress meter feels live but not so
    // often that we hammer the DB while a multi-hour encode runs.
    refetchInterval: 3_000,
  });
  const save = useMutation({
    mutationFn: (body: Parameters<typeof api.updateOptimize>[0]) => api.updateOptimize(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "optimize"] }),
  });

  if (q.isPending) return <div className="mb-6 rounded border border-zinc-800 bg-zinc-900 p-4 text-sm text-zinc-500">Loading optimizer…</div>;
  if (q.isError) return <div className="mb-6 rounded border border-zinc-800 bg-zinc-900 p-4 text-sm text-red-300">Optimizer error: {String(q.error)}</div>;
  const d = q.data;
  const cfg = d.config;
  const oneMinLoad = d.loadAvg[0] ?? 0;
  const overLoad = oneMinLoad > cfg.loadThreshold;
  const totalVideos = d.stats.pending + d.stats.optimized + d.stats.skipped;
  const pct = totalVideos > 0 ? Math.round((d.stats.optimized / totalVideos) * 100) : 0;

  return (
    <div className="mb-6 rounded border border-zinc-800 bg-zinc-900 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-base font-medium">Video optimization (idle-time re-encode)</h3>
        <span className="text-xs text-zinc-500">
          1-min load: <span className={overLoad ? "text-amber-300" : "text-zinc-300"}>{oneMinLoad.toFixed(2)}</span>
          {" / "}
          threshold {cfg.loadThreshold} · {d.cores} cores
        </span>
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        Runs in the background when no users are active and load is below the threshold. Each
        candidate is re-encoded with libx264/h264_vaapi at CRF {cfg.crf}; only kept if the new
        file is at least {cfg.minSavingsPct}% smaller. The atomic install
        replaces the original in place — no backup copy is kept, since
        keeping both would defeat the space-saving purpose.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3 lg:grid-cols-6">
        <Stat
          label="Status"
          value={
            d.stats.active
              ? "encoding"
              : cfg.enabled
                ? overLoad
                  ? "paused (load)"
                  : "ready"
                : "off"
          }
        />
        <Stat label="Optimized" value={d.stats.optimized.toLocaleString()} hint={`${pct}% of library`} />
        <Stat
          label="Eligible"
          value={d.stats.eligible.toLocaleString()}
          hint="worker will pick these next"
        />
        <Stat
          label="Not yet optimized"
          value={d.stats.pending.toLocaleString()}
          hint={`${(d.stats.pending - d.stats.eligible).toLocaleString()} ineligible (small/native)`}
        />
        <Stat label="Skipped" value={d.stats.skipped.toLocaleString()} />
        <Stat label="Bytes saved" value={formatBytes(d.stats.bytesSaved)} />
      </div>

      {d.stats.active && (
        <ActiveEncodeBanner active={d.stats.active} />
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3 text-xs">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => save.mutate({ enabled: e.target.checked })}
            className="h-4 w-4"
          />
          <span>Enabled</span>
        </label>
        <NumField
          label="Load threshold"
          value={cfg.loadThreshold}
          step={0.5}
          min={0}
          max={1024}
          onCommit={(v) => save.mutate({ loadThreshold: v })}
        />
        <NumField
          label="Idle seconds"
          value={cfg.idleSeconds}
          step={5}
          min={0}
          max={3600}
          onCommit={(v) => save.mutate({ idleSeconds: v })}
        />
        <NumField
          label="Min savings %"
          value={cfg.minSavingsPct}
          step={1}
          min={0}
          max={100}
          onCommit={(v) => save.mutate({ minSavingsPct: v })}
        />
        <NumField
          label="CRF"
          value={cfg.crf}
          step={1}
          min={18}
          max={32}
          onCommit={(v) => save.mutate({ crf: v })}
        />
        <NumField
          label="Min size (MB)"
          value={Math.round(cfg.minSizeBytes / (1024 * 1024))}
          step={50}
          min={0}
          max={102400}
          onCommit={(v) => save.mutate({ minSizeBytes: v * 1024 * 1024 })}
        />
      </div>

      {d.stats.recentSkips.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">
            Recently skipped ({d.stats.recentSkips.length})
          </summary>
          <ul className="mt-1 space-y-1 font-mono text-zinc-400">
            {d.stats.recentSkips.map((s) => (
              <li key={s.id} className="truncate">
                <span className="text-zinc-600">[{s.reason}]</span> {s.relativePath}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

interface ActiveProgress {
  mediaId: number;
  relativePath: string;
  originalSizeBytes: number;
  durationMs: number | null;
  startedAt: string;
  encodedMs: number;
  fps: number | null;
  speed: number | null;
}

function ActiveEncodeBanner({ active }: { active: ActiveProgress }) {
  const pct =
    active.durationMs && active.durationMs > 0
      ? Math.min(100, Math.round((active.encodedMs / active.durationMs) * 100))
      : null;
  const elapsedSec = Math.max(1, Math.round((Date.now() - new Date(active.startedAt).getTime()) / 1000));
  const etaSec =
    active.durationMs && active.encodedMs > 0 && active.speed && active.speed > 0
      ? Math.round((active.durationMs - active.encodedMs) / 1000 / active.speed)
      : null;
  return (
    <div className="mt-3 rounded border border-emerald-700/40 bg-emerald-950/30 p-3 text-xs">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-emerald-200">Encoding now</div>
          <div className="truncate font-mono text-zinc-400" title={active.relativePath}>
            {active.relativePath}
          </div>
        </div>
        <div className="text-right text-zinc-300">
          {formatBytes(active.originalSizeBytes)} · CRF / VAAPI
        </div>
      </div>
      {pct != null ? (
        <>
          <div className="h-2 overflow-hidden rounded bg-zinc-800">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-zinc-500">
            <span>
              {pct}% · {formatDurationMs(active.encodedMs)} of{" "}
              {formatDurationMs(active.durationMs ?? 0)}
            </span>
            <span>
              {active.speed ? `${active.speed.toFixed(2)}× speed` : ""}
              {active.fps ? ` · ${active.fps.toFixed(0)} fps` : ""}
              {etaSec != null ? ` · ETA ${formatDurationSec(etaSec)}` : ""}
            </span>
          </div>
        </>
      ) : (
        <div className="text-zinc-400">
          Encoding started {elapsedSec}s ago
          {active.speed ? ` · ${active.speed.toFixed(2)}× speed` : ""}
          {active.fps ? ` · ${active.fps.toFixed(0)} fps` : ""}
          {" — duration unknown, progress will resolve once ffmpeg reports a frame"}
        </div>
      )}
    </div>
  );
}

function formatDurationMs(ms: number): string {
  return formatDurationSec(Math.floor(ms / 1000));
}
function formatDurationSec(total: number): string {
  if (!Number.isFinite(total) || total < 0) return "?";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded bg-zinc-800/40 px-3 py-2">
      <div className="text-zinc-500">{label}</div>
      <div className="text-base text-zinc-100">{value}</div>
      {hint && <div className="text-[10px] text-zinc-500">{hint}</div>}
    </div>
  );
}

function NumField({
  label,
  value,
  step,
  min,
  max,
  onCommit,
}: {
  label: string;
  value: number;
  step: number;
  min: number;
  max: number;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  // Keep the draft synced if the parent value updates (e.g. polling).
  useEffect(() => setDraft(String(value)), [value]);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-zinc-500">{label}</span>
      <input
        type="number"
        value={draft}
        step={step}
        min={min}
        max={max}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const n = Number(draft);
          if (Number.isFinite(n) && n !== value) onCommit(n);
          else setDraft(String(value));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const n = Number(draft);
            if (Number.isFinite(n) && n !== value) onCommit(n);
          }
        }}
        className="w-24 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-zinc-200"
      />
    </label>
  );
}

function formatBytes(b: number): string {
  if (!Number.isFinite(b) || b <= 0) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
